/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import AvalaraValidationModal from "@eisinc/App/Components/AddressSuggestionCard/AvalaraValidationModal";
import { doScroll } from "@eisinc/App/Utilities/helpers";
import StepperContextProvider from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { Component, createContext, createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    clearMessages,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    // XNG-Changes
    updateBillingState: (address: BillToModel, county: any) => (dispatch: Dispatch<AnyAction>) => {
        const properties = { ...address.properties, county };

        dispatch({
            type: "Pages/CheckoutShipping/SetBillingAddress",
            address: {
                ...address,
                properties,
            },
        });
    },
    updateShippingState: (address: ShipToModel, county: any) => (dispatch: Dispatch<AnyAction>) => {
        const properties = { ...address.properties, county };

        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address: {
                ...address,
                properties,
            },
        });
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    cartId: state.pages.checkoutShipping.cartId,
    isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
    reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
    location: getLocation(state),
    isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
    session: state.context.session,
    // XNG-Changes
    billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState,
    shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    avalaraTitle?: string;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    avalaraValidation: any = createRef();

    constructor(props: Props) {
        super(props);

        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            avalaraTitle: "",
        };
    }

    componentDidMount() {
        setTimeout(async () => {
            if (!this.props.isPreloadingData) {
                const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
                await this.props.preloadCheckoutShippingData({ cartId });
                this.props.setInitialValues();
                this.props.setIsPreloadingData({ isPreloadingData: false });
                this.props.clearMessages();
            }
            this.props.setIsPreloadingData({ isPreloadingData: false });
            this.props.clearMessages();
            this.props.setInitialValues();
        });
    }

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!this.props.session.isAuthenticated) {
            window.location.reload();
            return;
        }

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        // XNF-Change use avlara validation
        if (results.every(o => o)) {
            this.setState({ isRedirecting: true });
            if (this.props.currentUserIsGuest) {
                await this.checkBillingAlavara();
            } else if (this.props.shippingAddressFormState?.address.oneTimeAddress) {
                await this.checkShippingAlavara();
            } else {
                await this.doUpdateCart();
            }
        } else {
            doScroll();
        }
    };

    // XNG-Changes
    checkBillingAlavara = async () => {
        try {
            this.setState({ avalaraTitle: translate("Billing Address Suggestions") });

            const updatedAddress: BillToModel = await this.avalaraValidation.current.validate(
                this.props.billingAddressFormState?.address,
            );

            this.avalaraValidation.current.countyValidation(
                updatedAddress,
                async (county: any) => {
                    this.props.updateBillingState(updatedAddress, county);
                    await this.checkShippingAlavara();
                },
                "checkoutShippingPageCheckBilling",
            );
        } catch (e) {
            this.setState({ isRedirecting: false });
        }
    };

    checkShippingAlavara = async () => {
        if (!this.props.shippingAddressFormState?.address.oneTimeAddress) {
            await this.doUpdateCart();

            return;
        }

        try {
            this.setState({ avalaraTitle: translate("Shipping Address Suggestions") });

            const updatedAddress: ShipToModel = await this.avalaraValidation.current.validate(
                this.props.shippingAddressFormState?.address,
            );

            this.avalaraValidation.current.countyValidation(updatedAddress, async (county: any) => {
                this.props.updateShippingState(updatedAddress, county);
                await this.doUpdateCart();
            });
        } catch (e) {
            this.setState({ isRedirecting: false });
        }
    };

    doUpdateCart = async () => {
        // TODO ISC-12556
        await this.props.updateCart({ fullExpand: true });
        const continueUrl = this.props.cartId
            ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
            : this.props.reviewAndSubmitPageLink!.url;
        this.props.history.push(continueUrl);
    };

    render() {
        return (
            <StepperContextProvider>
                <AvalaraValidationModal
                    ref={this.avalaraValidation}
                    title={this.state.avalaraTitle}
                    address={this.props.shippingAddressFormState?.address}
                />
                <Page data-test-selector="checkoutShipping">
                    <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                        <CheckoutShippingFormContext.Provider value={this.state}>
                            <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                                <Zone zoneName="Content" contentId={this.props.id} />
                            </form>
                        </CheckoutShippingFormContext.Provider>
                    </LoadingOverlay>
                </Page>
            </StepperContextProvider>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
